import React from "react";
import {
    Accordion,
    Badge,
    Group,
    Paper,
    Stack,
    Text,
    ThemeIcon,
} from "@mantine/core";
import { IconCalendar, IconClock, IconTicket } from "@tabler/icons-react";
import type { TicketDto } from "../types";
import type { Texts } from "../texts";

type Props = {
    tickets: TicketDto[];
    t: Texts;
};

const formatDate = (date: string | Date) =>
    new Date(date).toLocaleDateString("da-DK", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });

const formatTime = (date: string | Date) =>
    new Date(date).toLocaleTimeString("da-DK", {
        hour: "2-digit",
        minute: "2-digit",
    });

export default function TicketsSection({ tickets, t }: Props) {
    if (tickets.length === 0) return null;

    return (
        <Accordion.Item value="ticket">
            <Accordion.Control
                icon={
                    <ThemeIcon color="red" variant="light" radius="xl">
                        <IconTicket size={18} />
                    </ThemeIcon>
                }
            >
                {t.ticket}
            </Accordion.Control>

            <Accordion.Panel>
                <Stack gap="sm">
                    {tickets.map((ticket, index) => {
                        const start = ticket.ticketTimeStart;
                        const end = ticket.ticketTimeEnd;

                        const sameDay =
                            start && end
                                ? new Date(start).toDateString() ===
                                new Date(end).toDateString()
                                : false;

                        return (
                            <Paper
                                key={ticket.ticketID ?? index}
                                withBorder
                                p="md"
                                radius="md"
                            >
                                <Group
                                    justify="space-between"
                                    align="flex-start"
                                    wrap="nowrap"
                                >
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <Text fw={600} lineClamp={2}>
                                            {ticket.ticketDescription || "-"}
                                        </Text>

                                        {start && (
                                            <Stack gap={5} mt="sm">
                                                <Group gap={7} wrap="nowrap">
                                                    <IconCalendar
                                                        size={16}
                                                        color="var(--mantine-color-red-6)"
                                                    />
                                                    <Text size="sm" fw={500}>
                                                        {formatDate(start)}
                                                    </Text>
                                                </Group>

                                                {end && (
                                                    <>
                                                        {!sameDay && (
                                                            <Group gap={7} wrap="nowrap">
                                                                <IconCalendar
                                                                    size={16}
                                                                    color="var(--mantine-color-red-6)"
                                                                />
                                                                <Text size="sm" fw={500}>
                                                                    til {formatDate(end)}
                                                                </Text>
                                                            </Group>
                                                        )}

                                                        <Group gap={7} wrap="nowrap">
                                                            <IconClock
                                                                size={16}
                                                                color="var(--mantine-color-dimmed)"
                                                            />
                                                            <Text size="sm" c="dimmed">
                                                                Kl. {formatTime(start)} - {formatTime(end)}
                                                            </Text>
                                                        </Group>
                                                    </>
                                                )}
                                            </Stack>
                                        )}

                                        {ticket.sourceType && (
                                            <Text size="xs" c="dimmed" mt="sm">
                                                {t.source}: {ticket.sourceType}
                                            </Text>
                                        )}
                                    </div>

                                    <Badge
                                        color={ticket.isUsed ? "gray" : "red"}
                                        variant="light"
                                        style={{ flexShrink: 0 }}
                                    >
                                        {ticket.isUsed ? t.used : t.active}
                                    </Badge>
                                </Group>
                            </Paper>
                        );
                    })}
                </Stack>
            </Accordion.Panel>
        </Accordion.Item>
    );
}
